/**
 * MKS-292 — the board-level state behind the red hover highlight.
 *
 * Hovering one card's "Blocked by" / "Blocks" line has to ring OTHER cards,
 * which the hovered card cannot reach on its own, so the hovered counterpart
 * ids live here and every `TaskCard` reads them. Ids, not codes: codes are not
 * unique across projects, ids always are.
 *
 * `useBlockerHighlight` falls back to an inert value when no provider is
 * mounted, so a `TaskCard` still renders standalone (the drag overlay, tests,
 * the external portal). A counterpart that is not on the board simply never
 * matches an id — that is the whole of the "blocker in another project, behind
 * a column filter or archived" case: nothing breaks, and the line's tooltip
 * still names it.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface BlockerHighlightValue {
  /** Is this card one of the counterparts of the currently hovered line? */
  isHighlighted(taskId: number): boolean;
  /** Called on pointer enter with the hovered line's counterpart ids. */
  highlight(taskIds: readonly number[]): void;
  /** Called on pointer leave / blur. */
  clearHighlight(): void;
}

const INERT: BlockerHighlightValue = {
  isHighlighted: () => false,
  highlight: () => {},
  clearHighlight: () => {},
};

const BlockerHighlightContext = createContext<BlockerHighlightValue | null>(null);

export function BlockerHighlightProvider({ children }: { children: ReactNode }) {
  const [highlighted, setHighlighted] = useState<ReadonlySet<number>>(() => new Set<number>());

  const highlight = useCallback((taskIds: readonly number[]) => {
    setHighlighted(new Set(taskIds));
  }, []);

  const clearHighlight = useCallback(() => {
    // Keep the identity of the empty set stable so an already-clear board does
    // not re-render every card on every pointer leave.
    setHighlighted((current) => (current.size === 0 ? current : new Set<number>()));
  }, []);

  const value = useMemo<BlockerHighlightValue>(
    () => ({
      isHighlighted: (taskId: number) => highlighted.has(taskId),
      highlight,
      clearHighlight,
    }),
    [highlighted, highlight, clearHighlight],
  );

  return (
    <BlockerHighlightContext.Provider value={value}>{children}</BlockerHighlightContext.Provider>
  );
}

export function useBlockerHighlight(): BlockerHighlightValue {
  return useContext(BlockerHighlightContext) ?? INERT;
}
